// Feature 12: Provenance DAG Component

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Storage,
  Sensors,
  Lightbulb,
  Transform,
  ArrowForward,
} from '@mui/icons-material';
import type { ProvenanceDAG, ProvenanceNode } from '../../types/insightExplainability.types';

interface ProvenanceDAGProps {
  dag: ProvenanceDAG;
  highlightPath?: string[];
  onNodeClick?: (nodeId: string) => void;
}

const ProvenanceDAG: React.FC<ProvenanceDAGProps> = ({ dag, highlightPath = [], onNodeClick }) => {
  const getNodeIcon = (nodeType: string) => {
    switch (nodeType) {
      case 'DATA_SOURCE':
        return <Storage fontSize="small" />;
      case 'SIGNAL':
        return <Sensors fontSize="small" />;
      case 'INSIGHT':
        return <Lightbulb fontSize="small" />;
      case 'TRANSFORMATION':
        return <Transform fontSize="small" />;
      default:
        return <Sensors fontSize="small" />;
    }
  };

  const getNodeColor = (nodeType: string) => {
    switch (nodeType) {
      case 'DATA_SOURCE':
        return 'primary';
      case 'SIGNAL':
        return 'secondary';
      case 'INSIGHT':
        return 'success';
      case 'TRANSFORMATION':
        return 'info';
      default:
        return 'default';
    }
  };

  // Group nodes by type for layered visualization
  const nodesByType = dag.nodes.reduce((acc, node) => {
    if (!acc[node.nodeType]) acc[node.nodeType] = [];
    acc[node.nodeType].push(node);
    return acc;
  }, {} as Record<string, ProvenanceNode[]>);

  const layers = ['DATA_SOURCE', 'SIGNAL', 'TRANSFORMATION', 'INSIGHT'];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Provenance Graph
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
        Traces how this insight was derived from raw data
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>How to read:</strong> Data flows from left (sources) to right (insight). Each node
          represents a data processing step.
        </Typography>
      </Alert>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            {layers.map((layerType, layerIndex) => {
              const layerNodes = nodesByType[layerType] || [];
              if (layerNodes.length === 0) return null;

              return (
                <React.Fragment key={layerType}>
                  <Box flex={1}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1, textTransform: 'uppercase' }}
                    >
                      {layerType.replace('_', ' ')}
                    </Typography>
                    <Stack spacing={1.5}>
                      {layerNodes.map((node) => {
                        const isHighlighted = highlightPath.includes(node.id);
                        const hasConfidence = node.confidence !== undefined;

                        return (
                          <Tooltip
                            key={node.id}
                            title={
                              <>
                                <Typography variant="body2">{node.description}</Typography>
                                {hasConfidence && (
                                  <>
                                    <br />
                                    Confidence: {Math.round(node.confidence! * 100)}%
                                  </>
                                )}
                              </>
                            }
                          >
                            <Card
                              variant={isHighlighted ? 'elevation' : 'outlined'}
                              elevation={isHighlighted ? 4 : 0}
                              sx={{
                                cursor: onNodeClick ? 'pointer' : 'default',
                                transition: 'all 0.2s',
                                border: isHighlighted ? 2 : 1,
                                borderColor: isHighlighted ? 'primary.main' : 'divider',
                                '&:hover': onNodeClick
                                  ? {
                                      boxShadow: 2,
                                      borderColor: 'primary.light',
                                    }
                                  : {},
                              }}
                              onClick={() => onNodeClick?.(node.id)}
                            >
                              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                  {getNodeIcon(node.nodeType)}
                                  <Typography variant="body2" fontWeight={500}>
                                    {node.label}
                                  </Typography>
                                </Box>
                                {hasConfidence && (
                                  <Chip
                                    label={`${Math.round(node.confidence! * 100)}%`}
                                    size="small"
                                    color={getNodeColor(node.nodeType) as any}
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                              </CardContent>
                            </Card>
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  </Box>

                  {/* Arrow between layers */}
                  {layerIndex < layers.length - 1 && (
                    <Box display="flex" alignItems="center" pt={4}>
                      <ArrowForward color="action" />
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
          </Stack>

          {/* Edge information */}
          <Box mt={3} pt={2} borderTop={1} borderColor="divider">
            <Typography variant="caption" color="text.secondary">
              {dag.edges.length} relationships • Computed:{' '}
              {new Date(dag.computedAt).toLocaleString()}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProvenanceDAG;
